/**
 * Journal d'audit (`GET /admin/audit`).
 *
 * Une ligne par écriture (`card.update`, `balance.update`, `level.update`) et par publication
 * (`content.publish`). L'API assainit déjà `avant`/`apres` (clés sensibles masquées, adresses
 * tronquées) ; l'affichage applique une seconde passe de masquage, parce qu'un journal se lit
 * souvent en capture d'écran.
 *
 * L'acteur est affiché par son identifiant tronqué : le journal ne porte pas d'adresse en clair,
 * et rien ici ne va la chercher.
 */
import { Fragment, useState } from 'react';

import { listerAudit } from '../api/admin';
import { M } from '../catalogue/messages';
import { useChargement } from '../hooks/use-chargement';
import { BandeauErreur } from '../ui/Bandeau';
import { Bouton } from '../ui/Bouton';
import { Chargement } from '../ui/Chargement';
import { Coquille } from '../ui/Coquille';
import { Panneau } from '../ui/Panneau';
import { formaterDate, jsonLisible, libelleAction } from '../utils/presentation';

/** Taille de page confortable pour un journal (le plafond du contrat est 200). */
const PAR_PAGE = 50;

export function EcranAudit() {
  const [page, setPage] = useState(0);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const chargement = useChargement(
    (signal) => listerAudit({ limit: PAR_PAGE, offset: page * PAR_PAGE }, signal),
    [page],
  );

  const lignes = chargement.donnees?.logs ?? [];
  const nombreDePages = Math.max(1, page + (lignes.length === PAR_PAGE ? 2 : 1));

  return (
    <Coquille
      titre={M.audit.titre}
      sousTitre={M.audit.sousTitre}
      actions={
        <Bouton variante="secondaire" onClick={chargement.recharger}>
          {M.commun.actualiser}
        </Bouton>
      }
    >
      <Panneau>
        {chargement.erreur === null ? null : (
          <BandeauErreur erreur={chargement.erreur} surReessai={chargement.recharger} />
        )}
        {chargement.chargement ? <Chargement texte={M.commun.chargementListe} /> : null}

        {chargement.chargement || chargement.erreur !== null ? null : (
          <>
            <table className="tableau">
              <thead>
                <tr>
                  <th scope="col">{M.audit.colonneDate}</th>
                  <th scope="col">{M.audit.colonneActeur}</th>
                  <th scope="col">{M.audit.colonneAction}</th>
                  <th scope="col">{M.audit.colonneCible}</th>
                  <th scope="col">{M.commun.details}</th>
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne) => {
                  const estOuvert = ouvert === ligne.id;
                  return (
                    <Fragment key={ligne.id}>
                      <tr>
                        <td>{formaterDate(ligne.createdAt)}</td>
                        <td>
                          <code title={ligne.actorUserId ?? M.audit.acteurSupprime}>
                            {ligne.actorUserId === null
                              ? M.audit.acteurSupprime
                              : `${ligne.actorUserId.slice(0, 8)}…`}
                          </code>
                        </td>
                        <td>{libelleAction(ligne.action)}</td>
                        <td>
                          <code>{ligne.target}</code>
                        </td>
                        <td>
                          <Bouton
                            variante="discret"
                            onClick={() => {
                              setOuvert(estOuvert ? null : ligne.id);
                            }}
                            aria-expanded={estOuvert}
                          >
                            {estOuvert ? M.commun.masquer : M.commun.details}
                          </Bouton>
                        </td>
                      </tr>
                      {estOuvert ? (
                        <tr key={`${ligne.id}-detail`} className="tableau__ligne-detail">
                          <td colSpan={5}>
                            <div className="detail-audit">
                              <div>
                                <h3 className="detail-audit__titre">{M.audit.avant}</h3>
                                <pre className="detail-audit__json">{jsonLisible(ligne.avant)}</pre>
                              </div>
                              <div>
                                <h3 className="detail-audit__titre">{M.audit.apres}</h3>
                                <pre className="detail-audit__json">{jsonLisible(ligne.apres)}</pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>

            {lignes.length === 0 ? <p className="etat-vide">{M.commun.aucunResultat}</p> : null}

            <div className="pagination">
              <Bouton
                variante="secondaire"
                onClick={() => {
                  setPage((courante) => Math.max(0, courante - 1));
                }}
                disabled={page === 0}
              >
                {M.commun.precedent}
              </Bouton>
              <span className="pagination__etat">{M.commun.page(page + 1, nombreDePages)}</span>
              <Bouton
                variante="secondaire"
                onClick={() => {
                  setPage((courante) => courante + 1);
                }}
                disabled={lignes.length < PAR_PAGE}
              >
                {M.commun.suivant}
              </Bouton>
            </div>
          </>
        )}
      </Panneau>
    </Coquille>
  );
}
